import { useState, type FormEvent } from "react";
import { toast } from "react-hot-toast";

import { useAuth } from "../auth/AuthContext";
import { canAdministerWorkspace, isIsaraStaff } from "../auth/roles";
import {
  Button,
  ConfirmDialog,
  FieldRow,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Selector,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
  TrashButton,
} from "../components/ui";
import {
  Empty,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
} from "../components/WorkspacePrimitives";
import SettingsLayout from "./SettingsLayout";
import {
  useCreateWorkspaceUser,
  useReactivateWorkspaceUser,
  useResendWorkspaceInvitation,
  useSuspendWorkspaceUser,
  useUpdateWorkspaceUser,
  useWorkspaceUsers,
  type WorkspaceAccessLevel,
  type WorkspaceUser,
} from "./workspaceUsers";

const ACCESS_OPTIONS = [
  { value: "member", label: "Member" },
  { value: "admin", label: "Admin" },
] as const;

const STAFF_ACCESS_OPTIONS = [
  ...ACCESS_OPTIONS,
  { value: "super_admin", label: "Super admin" },
] as const;

function visibleEmail(email: string | null | undefined): string | null {
  return email?.toLowerCase().endsWith("@external.invalid")
    ? null
    : email || null;
}

function userLabel(user: WorkspaceUser): string {
  return user.display_name || visibleEmail(user.email) || "Workspace user";
}

function UserIdentity({ user }: { user: WorkspaceUser }) {
  const email = visibleEmail(user.email);
  return (
    <div className="token-name">
      <strong>{user.display_name || email || "Workspace user"}</strong>
      {user.display_name && email && <small>{email}</small>}
    </div>
  );
}

function UserActions({ user }: { user: WorkspaceUser }) {
  const auth = useAuth();
  const update = useUpdateWorkspaceUser();
  const suspend = useSuspendWorkspaceUser();
  const reactivate = useReactivateWorkspaceUser();
  const resend = useResendWorkspaceInvitation();
  const [confirmingSuspend, setConfirmingSuspend] = useState(false);
  if (user.access_level === "super_admin" && !auth.canOperate) return null;
  if (user.is_current_user) {
    return (
      <div className="workspace-user-actions">
        <span className="settings-you">You</span>
      </div>
    );
  }

  const label = userLabel(user);
  const email = visibleEmail(user.email);
  const mutate = (action: "reactivate" | "resend") => {
    const mutation = action === "reactivate" ? reactivate : resend;
    mutation.mutate(user.subject, {
      onSuccess: () =>
        toast.success(
          action === "reactivate" ? "User reactivated." : "Invitation resent.",
        ),
      onError: (error) => toast.error(error.message),
    });
  };

  const handleSuspend = () => {
    suspend.mutate(user.subject, {
      onSuccess: () => {
        toast.success("User suspended and API tokens revoked.");
        setConfirmingSuspend(false);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  return (
    <>
      <div className="workspace-user-actions">
        {user.status === "invited" && (
          <Button tone="quiet" onClick={() => mutate("resend")}>
            Resend invite
          </Button>
        )}
        {user.status === "suspended" ? (
          <Button onClick={() => mutate("reactivate")}>Reactivate</Button>
        ) : (
          <TrashButton
            label={`Suspend ${email ?? label}`}
            disabled={suspend.isPending}
            onClick={() => setConfirmingSuspend(true)}
          />
        )}
        <Selector
          ariaLabel={`Access for ${email ?? label}`}
          options={
            canAdministerWorkspace(auth) &&
            isIsaraStaff(true, {
              subject: user.subject,
              email: user.email ?? undefined,
            })
              ? STAFF_ACCESS_OPTIONS
              : ACCESS_OPTIONS
          }
          value={user.access_level}
          onValueChange={(value) =>
            update.mutate(
              { subject: user.subject, update: { access_level: value } },
              {
                onSuccess: () => toast.success("Access updated."),
                onError: (error) => toast.error(error.message),
              },
            )
          }
        />
      </div>
      {confirmingSuspend && (
        <ConfirmDialog
          title={`Suspend ${label}?`}
          description="Their E2E access will stop immediately and their API tokens will be revoked."
          confirmLabel="Suspend user"
          busy={suspend.isPending}
          onCancel={() => setConfirmingSuspend(false)}
          onConfirm={handleSuspend}
        />
      )}
    </>
  );
}

function InviteUserModal({ onClose }: { onClose: () => void }) {
  const create = useCreateWorkspaceUser();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [accessLevel, setAccessLevel] =
    useState<WorkspaceAccessLevel>("member");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await create.mutateAsync({
        email: email.trim(),
        first_name: firstName.trim() || null,
        family_name: familyName.trim() || null,
        access_level: accessLevel,
      });
      toast.success("Workspace user added.");
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Invite failed.");
    }
  };

  return (
    <Modal size="compact" ariaLabelledBy="invite-user-title" onClose={onClose}>
      <ModalHeader
        eyebrow="Workspace users"
        title="Add user"
        titleId="invite-user-title"
        onClose={onClose}
      />
      <form onSubmit={submit}>
        <ModalBody className="token-create-form">
          <FieldRow label="Email">
            <TextInput
              value={email}
              onValueChange={setEmail}
              type="email"
              autoComplete="email"
              required
              autoFocus
            />
          </FieldRow>
          <FieldRow label="First name · optional">
            <TextInput
              value={firstName}
              onValueChange={setFirstName}
              maxLength={100}
              autoComplete="given-name"
            />
          </FieldRow>
          <FieldRow label="Family name · optional">
            <TextInput
              value={familyName}
              onValueChange={setFamilyName}
              maxLength={100}
              autoComplete="family-name"
            />
          </FieldRow>
          <FieldRow label="Access">
            <Selector
              ariaLabel="Workspace access"
              options={ACCESS_OPTIONS}
              value={accessLevel}
              onValueChange={setAccessLevel}
            />
          </FieldRow>
        </ModalBody>
        <ModalFooter>
          <Button tone="quiet" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button tone="primary" type="submit" disabled={create.isPending}>
            {create.isPending ? "Adding…" : "Add user"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default function WorkspaceUsersPage() {
  const auth = useAuth();
  const users = useWorkspaceUsers();
  const [inviting, setInviting] = useState(false);

  return (
    <SettingsLayout>
      <div className="page page--workspace token-settings-page">
        <PageHeader className="settings-page-header">
          <div>
            <h1>Users</h1>
          </div>
          {canAdministerWorkspace(auth) && (
            <Button tone="primary" onClick={() => setInviting(true)}>
              Add user
            </Button>
          )}
        </PageHeader>
        <Panel
          title="Workspace users"
          meta={users.data ? `${users.data.length} total` : undefined}
        >
          {users.isPending && !users.data ? (
            <Loading label="Loading workspace users…" />
          ) : users.error && !users.data ? (
            <ErrorState error={users.error} onRetry={() => users.refetch()} />
          ) : !users.data || users.data.length === 0 ? (
            <Empty>No users belong to this workspace.</Empty>
          ) : (
            <TableContainer>
              <Table
                className="workspace-user-table"
                aria-label="Workspace users"
              >
                <TableHead>
                  <TableRow>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Access</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Added</TableHeader>
                    {canAdministerWorkspace(auth) && (
                      <TableHeader aria-label="Actions" />
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.data.map((user) => (
                    <TableRow key={user.subject}>
                      <TableCell>
                        <UserIdentity user={user} />
                      </TableCell>
                      <TableCell>
                        {user.access_level === "super_admin"
                          ? "Super admin"
                          : user.access_level === "admin"
                            ? "Admin"
                            : "Member"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`token-status token-status--${user.status}`}
                        >
                          {user.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      {canAdministerWorkspace(auth) && (
                        <TableCell>
                          <UserActions user={user} />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Panel>
        {inviting && <InviteUserModal onClose={() => setInviting(false)} />}
      </div>
    </SettingsLayout>
  );
}
